import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bell, UserPlus, Bike, Clock, Zap, CheckCheck, Trash2, X } from "lucide-react";
import { BottomNav } from "@/components/BottomNav";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { useNotifications } from "@/lib/notifications";
import i18n from "@/i18n";

export const Route = createFileRoute("/activity")({
  component: Activity,
});

interface Notif {
  id: string;
  type: string;
  title: string;
  body: string;
  data: any;
  read: boolean;
  created_at: string;
}

const TYPE_META: Record<string, { Icon: any; color: string; bg: string }> = {
  rider_joined: { Icon: UserPlus, color: "#F5C518", bg: "#F5C518" },
  new_ride_nearby: { Icon: Bike, color: "#4caf7d", bg: "#4caf7d" },
  ride_starting_soon: { Icon: Clock, color: "#e07b3a", bg: "#e07b3a" },
  compatible_ride: { Icon: Zap, color: "#F5C518", bg: "#F5C518" },
  ride_updated: { Icon: Clock, color: "#e07b3a", bg: "#e07b3a" },
};

function relative(iso: string) {
  const diff = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (diff < 1) return i18n.t("common.now");
  if (diff < 60) return i18n.t("common.minAgo", { n: diff });
  const h = Math.floor(diff / 60);
  if (h < 24) return i18n.t("common.hAgo", { n: h });
  return i18n.t("common.dAgo", { n: Math.floor(h / 24) });
}

function Activity() {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { markAllRead } = useNotifications();
  const [items, setItems] = useState<Notif[] | null>(null);

  useEffect(() => { if (!loading && !user) navigate({ to: "/auth" }); }, [loading, user, navigate]);

  const load = async () => {
    if (!user) return;
    const { data } = await supabase
      .from("notifications")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(30);
    setItems((data as Notif[]) ?? []);
  };

  useEffect(() => {
    load();
    if (!user) return;
    const ch = supabase
      .channel(`activity-${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` },
        () => load()
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [user?.id]);

  const open = async (n: Notif) => {
    if (!n.read) {
      await supabase.from("notifications").update({ read: true }).eq("id", n.id);
    }
    const rideId = n.data?.ride_id;
    if (rideId) navigate({ to: "/ride/$rideId", params: { rideId } });
  };

  const removeOne = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setItems((prev) => (prev ? prev.filter((x) => x.id !== id) : prev));
    await supabase.from("notifications").delete().eq("id", id);
  };

  const clearAll = async () => {
    if (!user || !items?.length) return;
    if (!confirm(t("activity.confirmClearAll"))) return;
    setItems([]);
    await supabase.from("notifications").delete().eq("user_id", user.id);
  };

  return (
    <div className="h-screen overflow-y-auto bg-background pb-28 fade-in">
      <header className="flex items-end justify-between gap-2 px-5 pt-[max(env(safe-area-inset-top),1rem)] pb-4">
        <h1 className="font-display text-3xl">{t("activity.title")}</h1>
        <div className="flex items-center gap-2">
          {(items?.some((n) => !n.read)) && (
            <button
              onClick={() => markAllRead().then(load)}
              className="press-scale flex items-center gap-1 rounded-full bg-surface-elevated px-3 py-1.5 text-xs font-medium text-white/80"
            >
              <CheckCheck className="h-3.5 w-3.5" /> {t("activity.markAll")}
            </button>
          )}
          {!!items?.length && (
            <button
              onClick={clearAll}
              className="press-scale flex items-center gap-1 rounded-full bg-surface-elevated px-3 py-1.5 text-xs font-medium text-red-400"
            >
              <Trash2 className="h-3.5 w-3.5" /> {t("activity.clearAll")}
            </button>
          )}
        </div>
      </header>

      <div className="px-4">
        {items === null && (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-16 animate-pulse rounded-2xl bg-[#1a1a1a]" />
            ))}
          </div>
        )}

        {items?.length === 0 && (
          <div className="rounded-3xl border border-border bg-[#1a1a1a] p-8 text-center">
            <Bell className="mx-auto h-10 w-10 text-primary" strokeWidth={1.8} />
            <p className="mt-3 text-sm text-white/70">{t("activity.noActivity")}</p>
            <p className="mt-1 text-xs text-white/40">{t("activity.noActivitySub")}</p>
          </div>
        )}

        <ul className="space-y-2">
          {items?.map((n) => {
            const meta = TYPE_META[n.type] ?? { Icon: Bell, color: "#F5C518", bg: "#F5C518" };
            const Icon = meta.Icon;
            return (
              <li key={n.id} className="relative">
                <button
                  onClick={() => open(n)}
                  className={`press-scale flex w-full items-start gap-3 rounded-2xl border border-border p-3 pr-10 text-left transition-colors ${
                    n.read ? "bg-[#1a1a1a]" : "bg-[#1f1f1f]"
                  }`}
                >
                  <span
                    className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                    style={{ background: `color-mix(in oklab, ${meta.bg} 18%, transparent)`, color: meta.color }}
                  >
                    <Icon className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="truncate font-display text-base font-bold">{n.title}</div>
                      <div className="shrink-0 text-[11px] text-white/40">{relative(n.created_at)}</div>
                    </div>
                    <div className="truncate text-sm text-white/60">{n.body}</div>
                  </div>
                  {!n.read && <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" />}
                </button>
                <button
                  onClick={(e) => removeOne(e, n.id)}
                  aria-label={t("activity.delete")}
                  className="press-scale absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/40 text-white/60 hover:text-red-400"
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <BottomNav />
    </div>
  );
}
